import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'

import { ApiError, PaginatedApiResult } from '@mangovoice/types'

interface PhoneNumber {
  id: number
  pbx_id?: number
  name: string
  number: string
  caller_identification?: string
  toll_free?: boolean
  call_endpoint?: string
  e911_registered_at?: string
  sms_activated_at?: string
  voice_provider?: string
  sms_provider?: string
  e911_information?: string
  sms_information?: string
  local_presence?: number
  extension?: string
  type?: string
  created_at?: string
  updated_at?: string
}

interface SwitchboardPhoneNumberOption {
  id: string
  name: string
  number: string
}

const mapPhoneNumbersToOptions = (
  response: PaginatedApiResult<PhoneNumber> | null | undefined,
): SwitchboardPhoneNumberOption[] => {
  return (
    response?.results?.map((phone) => ({
      id: String(phone.id),
      name: phone.name || `Phone ${phone.number}`,
      number: phone.number,
    })) || []
  )
}

/**
 * Fetches all phone numbers for a PBX that can be used in switchboard call flow nodes.
 * This fetches two datasets:
 * - All phone numbers (for External Transfer caller ID and routing)
 * - SMS-eligible phone numbers (for SMS "Send from" fallback options)
 *
 * Uses /app/v2/phone-numbers/ with and without sms_eligible=true.
 */
export const useGetPhoneNumbers = (pbxId: number | null) => {
  const api = useMangoApiClient()

  const { data: phoneNumbersResponse, isLoading: isLoadingPhoneNumbers } =
    useQuery<PaginatedApiResult<PhoneNumber> | null, ApiError>({
      queryKey: ['switchboard', 'phoneNumbers', pbxId],
      queryFn: async () => {
        if (!pbxId) return null

        const response = await api.switchboard.listPhoneNumbers({
          pbx_id: pbxId,
        })

        if (response.ok && response.data) {
          return response.data
        }

        return null
      },
      enabled: !!pbxId,
      retry: false,
    })

  const {
    data: smsEligiblePhoneNumbersResponse,
    isLoading: isLoadingSmsEligiblePhoneNumbers,
  } = useQuery<PaginatedApiResult<PhoneNumber> | null, ApiError>({
    queryKey: ['switchboard', 'phoneNumbers', pbxId, 'smsEligible'],
    queryFn: async () => {
      if (!pbxId) return null

      const response = await api.switchboard.listPhoneNumbers({
        pbx_id: pbxId,
        sms_eligible: true,
      })

      if (response.ok && response.data) {
        return response.data
      }

      return null
    },
    enabled: !!pbxId,
    retry: false,
  })

  const phoneNumbers = useMemo(() => {
    return mapPhoneNumbersToOptions(phoneNumbersResponse)
  }, [phoneNumbersResponse])

  const smsEligiblePhoneNumbers = useMemo(() => {
    return mapPhoneNumbersToOptions(smsEligiblePhoneNumbersResponse)
  }, [smsEligiblePhoneNumbersResponse])

  return {
    phoneNumbers,
    smsEligiblePhoneNumbers,
    isLoading: isLoadingPhoneNumbers || isLoadingSmsEligiblePhoneNumbers,
  }
}
